import { ProfileJoint, ProfileLine, ProfileLineType } from '@paragon/contract-library';
import { LineSegment2D, Point2D, point2DEquals } from '@paragon/geometry-library';
import { wrapErr, wrapOk } from '@paragon/utilities';

/**
 * An immutable view of a profile line that contains its geometric information for ease of access.
 */
export class ProfileLineClass {
  readonly lineData: ProfileLine;
  readonly guid: string;
  readonly profileLineType: ProfileLineType;

  readonly baseJoint: ProfileJoint;
  readonly endJoint: ProfileJoint;

  readonly segment: LineSegment2D;
  readonly basePoint: Point2D;
  readonly endPoint: Point2D;
  readonly length: number;

  constructor(profileLine: ProfileLine, baseJoint: ProfileJoint, endJoint: ProfileJoint) {
    this.lineData = profileLine;
    this.guid = profileLine.guid;
    this.profileLineType = profileLine.profileLineType;

    this.baseJoint = baseJoint;
    this.endJoint = endJoint;

    this.segment = LineSegment2D.fromPointObjects(this.baseJoint.location, this.endJoint.location);
    this.basePoint = this.segment.basePoint;
    this.endPoint = this.segment.endPoint;
    this.length = this.segment.length;
  }

  static fromProfileLineAndJoints(profileLine: ProfileLine, profileJoints: readonly ProfileJoint[]) {
    const baseJoint = profileJoints.find((joint) => joint.guid === profileLine.baseJointGuid);
    if (!baseJoint) return wrapErr('Could not find base joint.');

    const endJoint = profileJoints.find((joint) => joint.guid === profileLine.endJointGuid);
    if (!endJoint) return wrapErr('Could not find end joint.');

    if (point2DEquals(baseJoint.location, endJoint.location))
      return wrapErr('Profile line exists with end points at the same location.');

    const profileLineClass = new ProfileLineClass(profileLine, baseJoint, endJoint);
    return wrapOk(profileLineClass);
  }

  reverse() {
    const reversedLineData: ProfileLine = {
      ...this.lineData,
      baseJointGuid: this.lineData.endJointGuid,
      endJointGuid: this.lineData.baseJointGuid,
      webJointSegments: this.lineData.webJointSegments
        .map((segment) => ({ ...segment, baseJointGuid: segment.endJointGuid, endJointGuid: segment.baseJointGuid }))
        .toReversed(),
    };

    return new ProfileLineClass(reversedLineData, this.endJoint, this.baseJoint);
  }
}
